import { ConnectionPositionPair, Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal, ComponentType } from "@angular/cdk/portal";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, ElementRef, EventEmitter, forwardRef, Injector, Input, NgZone, OnDestroy, OnInit, Output, QueryList, Renderer2, TemplateRef, ViewChild, ViewRef, ContentChild, ContentChildren } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { AdMessageService } from "@internal/common-ui/ad-message";
import { Subscription } from "rxjs";
import { PanelComponentModel } from '../panel-component.model';

export const RICHRDROPDWON_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AdRichDropdownComponent),
    multi: true
}

@Component({
    selector: 'ad-richdropdown',
    templateUrl: './ad-rich-dropdown.component.html',
    styleUrls: ['./ad-rich-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [RICHRDROPDWON_VALUE_ACCESSOR]
})
export class AdRichDropdownComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @Input() style: any;

    @Input() styleClass: string;

    @Input() panelClass: string[] = [];

    @Input() panelSize: { [key in 'width' | 'height' | 'minWidth' | 'minHeight' | 'maxWidth' | 'maxHeight']?: number | string };

    @Input() placeholder: string;

    @Input() dropdownCollapsedIcon: string = 'fas fa-chevron-down';

    @Input() dropdownExpandedIcon: string = 'fas fa-chevron-up';

    @Input() showClear: boolean;

    @Input() hideOnSelect: boolean = true;

    @Input() panelComponent: ComponentType<ControlValueAccessor>;

    @Output() onChange: EventEmitter<any> = new EventEmitter();

    @Output() onClick: EventEmitter<any> = new EventEmitter();

    @Output() onShow: EventEmitter<any> = new EventEmitter();

    @Output() onHide: EventEmitter<any> = new EventEmitter();

    @ViewChild('container') containerViewChild: ElementRef;

    private _disabled: boolean;

    @Input() get disabled(): boolean {
        return this._disabled;
    };

    set disabled(_disabled: boolean) {
        this._disabled = _disabled;
        if (!(this.cd as ViewRef).destroyed) {
            this.cd.markForCheck();
        }
        if (this._disabled && this.overlayVisible) {
            this.hide();
        }
    }

    _panelComponentInputs: any;
    @Input()
    get panelComponentInputs(): any {
        return this._panelComponentInputs;
    }
    set panelComponentInputs(val: any) {
        this._panelComponentInputs = val;
        this.applyChangesToPanel();
    }

    value: any;

    label: string;

    onModelChange: Function = () => { };

    onModelTouched: Function = () => { };

    overlayVisible: boolean;

    optionsChanged: boolean;

    overlayRef: OverlayRef;

    dropBackListener: Subscription;

    component: ComponentRef<ControlValueAccessor>;

    constructor(public el: ElementRef,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public zone: NgZone,
        public overlay: Overlay,
        public injector: Injector,
        private adMessageService: AdMessageService) { }

    ngOnDestroy(): void {
        if (this.overlayVisible) {
            this.hide();
        }
    }


    writeValue(obj: any): void {
        this.value = obj;
        this.label = this.value?.label;
        this.forwardWriteValue();
        this.cd.detectChanges();
    }
    registerOnChange(fn: any): void {
        this.onModelChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onModelTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnInit(): void {
    }

    applyChangesToPanel() {
        if (this.component != null) {
            Object.assign(this.component.instance, this.panelComponentInputs || {});
            this.component.changeDetectorRef.markForCheck();
        }
    }

    getPanelForm(): ControlValueAccessor {
        if (this.component != null) {
            return this.component.instance;
        }
        return null;
    }

    onMouseclick(event) {
        if (this.disabled) {
            return;
        }
        this.onModelTouched();

        this.onClick.emit(event);

        if (this.overlayVisible)
            this.hide();
        else
            this.show();

        this.cd.detectChanges();
    }

    forwardWriteValue() {
        let panel = this.getPanelForm();
        if (panel != null) {
            panel.writeValue(this.value);
        }
    }

    onValueChangeLocalCall(val) {
        this.value = val;
        this.label = this.value?.label ?? this.value;;
        this.onModelChange(this.value);
        this.onChange.emit(this.value);
    }

    clear(event: Event) {
        this.value = null;
        this.label = '';
        this.forwardWriteValue();
        this.onValueChangeLocalCall(this.value);
        event.stopPropagation();
        event.preventDefault();
    }



    forwardOnModelChange() {
        let overlayForm = this.getPanelForm();
        if (overlayForm != null) {
            overlayForm.registerOnChange((val) => {
                this.onValueChangeLocalCall(val);
                if (this.hideOnSelect) {
                    this.hide();
                }
            });
            overlayForm.registerOnTouched(() => this.onModelTouched());
        }
    }

    show() {
        let width = (this.el.nativeElement as HTMLDivElement).offsetWidth;
        let inputs = { ...this.panelComponentInputs, style: { 'min-width': `${width}px`, 'width': `${width}px`, 'max-width': `${width * 1.5}px`, ...this.panelComponentInputs?.style } };

        const { overlayRef, componentRef } = this.adMessageService.openPanelComponent(this.panelComponent,
            null,
            null,
            null,
            this.getOverlayConfig(),
            inputs,
        );
        this.overlayRef = overlayRef;
        this.component = componentRef;
        this.dropBackListener = this.overlayRef.backdropClick().subscribe(
            param => {
                this.hide();
            }
        );
        this.forwardWriteValue();
        this.forwardOnModelChange();
        this.overlayVisible = true;
        this.onShow.emit();
    }

    hide() {
        if (this.dropBackListener != null) {
            this.dropBackListener.unsubscribe();
            this.dropBackListener = null;
        }
        if (this.overlayRef != null) {
            this.overlayRef.detach();
            this.overlayRef = null;
            this.component = null;
        }
        this.overlayVisible = false;
        this.onHide.emit();
        this.cd.markForCheck();
    }

    getOverlayConfig(): OverlayConfig {
        let result: OverlayConfig = {
            ...this.panelSize,
            panelClass: [...this.panelClass || [], 'rich-dropdown'],
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(this.containerViewChild)
                .withPositions([
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
                    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' }),
                ])
                .withPush(false),
            hasBackdrop: true,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            backdropClass: 'cdk-overlay-transparent-backdrop'
        }
        return result;
    }

}